import { useState, useRef, useEffect } from 'react';
import type { Message } from '@pirouette/shared';
import { useMessages, useSendMessage } from '@/data/messaging';
import { Button } from '@/components/ui/Button';
import { toast } from '@/lib/toast';
import { cn } from '@/lib/cn';

interface ThreadViewProps {
  threadId: string;
  // Role of the viewer — controls bubble alignment + author label.
  viewerRole: 'admin' | 'parent';
}

export function ThreadView({ threadId, viewerRole }: ThreadViewProps) {
  const messagesQuery = useMessages(threadId);
  const send = useSendMessage();
  const [draft, setDraft] = useState('');
  const scrollerRef = useRef<HTMLDivElement | null>(null);
  // Suppress the aria-live announcement until after first paint. Otherwise
  // screen readers announce every existing message individually when the
  // thread first opens (20-message backlog → 20 announcements).
  const [hasMounted, setHasMounted] = useState(false);
  useEffect(() => {
    setHasMounted(true);
  }, []);

  // Auto-scroll to latest message when the list grows.
  useEffect(() => {
    if (!scrollerRef.current) return;
    scrollerRef.current.scrollTop = scrollerRef.current.scrollHeight;
  }, [messagesQuery.data?.length]);

  async function handleSend() {
    const trimmed = draft.trim();
    if (!trimmed) return;
    try {
      await send.mutateAsync({ thread_id: threadId, body: trimmed });
      setDraft('');
    } catch (err) {
      toast({
        title: "Couldn't send message",
        description: err instanceof Error ? err.message : 'Unknown error',
        variant: 'error',
      });
    }
  }

  return (
    <div className="flex h-full flex-col">
      <div
        ref={scrollerRef}
        role="log"
        aria-live={hasMounted ? 'polite' : 'off'}
        aria-relevant="additions"
        className="flex-1 space-y-3 overflow-y-auto p-1"
      >
        {messagesQuery.isLoading && <p className="text-[var(--color-ink-subtle)]">Loading…</p>}
        {messagesQuery.data?.map((m) => (
          <MessageBubble key={m.id} message={m} viewerRole={viewerRole} />
        ))}
      </div>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          void handleSend();
        }}
        className="mt-4 flex flex-col gap-2"
      >
        <label htmlFor="msg-body" className="sr-only">
          Message body
        </label>
        <textarea
          id="msg-body"
          rows={3}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder="Type your message…"
          className="resize-y rounded-[var(--radius-md)] border border-[var(--color-border)] bg-[var(--color-surface)] p-3 text-[var(--text-base)] focus-visible:outline-none"
        />
        <div className="flex items-center justify-between">
          <p className="text-[var(--color-ink-subtle)] text-[var(--text-xs)]">
            {draft.length}/4000
          </p>
          <Button
            type="submit"
            disabled={!draft.trim() || send.isPending}
            aria-busy={send.isPending}
          >
            {send.isPending ? 'Sending…' : 'Send'}
          </Button>
        </div>
      </form>
    </div>
  );
}

function MessageBubble({
  message,
  viewerRole,
}: {
  message: Message;
  viewerRole: 'admin' | 'parent';
}) {
  const isOwn = message.author_role === viewerRole;
  return (
    <article
      className={cn('flex flex-col gap-1', isOwn ? 'items-end' : 'items-start')}
      aria-label={`Message from ${message.author_role}`}
    >
      <div
        className={cn(
          'max-w-[36rem] rounded-[var(--radius-lg)] px-4 py-2.5',
          isOwn
            ? 'bg-[var(--color-accent)] text-[var(--color-ink-on-accent)]'
            : 'border border-[var(--color-border)] bg-[var(--color-surface-elevated)] text-[var(--color-ink)]',
        )}
      >
        <p className="leading-[var(--leading-snug)] whitespace-pre-wrap text-[var(--text-base)]">
          {message.body}
        </p>
      </div>
      <p className="text-[var(--color-ink-subtle)] text-[var(--text-xs)]">
        {message.author_role} · {new Date(message.created_at).toLocaleString()}
      </p>
    </article>
  );
}
